import React, { useState, useMemo } from 'react';
import {
  Box, Paper, Typography, FormControl, InputLabel, Select, MenuItem,
  Checkbox, ListItemText, OutlinedInput, Slider, useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer, ReferenceArea, ReferenceLine,
} from 'recharts';
import { tokens } from '../theme';
import { useTranslation } from 'react-i18next';
import { getUnits, convertDistance, convertSpeed, convertElevation } from '../pages/WorkoutAnalysisV2';

const CLIMB_CATEGORY_FILL = {
  'B-C4': '#ffe066',
  'B-C3': '#ffa020',
  'B-C2': '#e05000',
  'B-C1': '#c01010',
  'B-HC': '#7a0000',
};

const DESCENT_CATEGORY_FILL = {
  'B-D4':  '#a0d0ff',
  'B-D3':  '#4090e0',
  'B-D2':  '#1060b0',
  'B-D1':  '#003880',
  'B-DHC': '#001840',
};

const DEFAULT_METRICS = ['altitude', 'speed'];

function smoothData(data, window) {
  if (window <= 1) return data;
  return data.map((point, i) => {
    const half = Math.floor(window / 2);
    const start = Math.max(0, i - half);
    const end = Math.min(data.length - 1, i + half);
    const slice = data.slice(start, end + 1);
    const smoothed = { ...point };
    Object.keys(point).forEach(k => {
      if (k !== 'distance' && typeof point[k] === 'number') {
        smoothed[k] = slice.reduce((s, p) => s + (p[k] || 0), 0) / slice.length;
      }
    });
    return smoothed;
  });
}

const BikeClimbsChart = ({
  chunks,
  climbs = [],
  descents = [],
  activeClimbRange = null,
  activeDescentRange = null,
  planSegments = [],
  activePlanSegment = null,
  externalZoom = null,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [units, setUnits] = useState(getUnits());
  React.useEffect(() => {
    const handler = (e) => setUnits(e.detail || getUnits());
    window.addEventListener('unitsChanged', handler);
    return () => window.removeEventListener('unitsChanged', handler);
  }, []);

  const [metrics, setMetrics] = useState(DEFAULT_METRICS);
  const [smoothingWindow, setSmoothingWindow] = useState(1);

  const [zoomLeft, setZoomLeft] = useState('dataMin');
  const [zoomRight, setZoomRight] = useState('dataMax');
  const [refLeft, setRefLeft] = useState(null);
  const [refRight, setRefRight] = useState(null);
  const [selecting, setSelecting] = useState(false);

  const resetZoom = () => { setZoomLeft('dataMin'); setZoomRight('dataMax'); setRefLeft(null); setRefRight(null); };

  // Sync external double-click zoom (e.g. from plan segment double-click)
  React.useEffect(() => {
    if (!externalZoom) {
      setZoomLeft('dataMin'); setZoomRight('dataMax');
      return;
    }
    const left = units === 'imperial' ? externalZoom.leftKm * 0.621371 : externalZoom.leftKm;
    const right = units === 'imperial' ? externalZoom.rightKm * 0.621371 : externalZoom.rightKm;
    setZoomLeft(left);
    setZoomRight(right);
  }, [externalZoom]); // eslint-disable-line react-hooks/exhaustive-deps
  const handleMouseDown = (e) => { if (e?.activeLabel != null) { setRefLeft(e.activeLabel); setRefRight(e.activeLabel); setSelecting(true); } };
  const handleMouseMove = (e) => { if (selecting && e?.activeLabel != null) setRefRight(e.activeLabel); };
  const handleMouseUp = () => {
    if (selecting && refLeft != null && refRight != null && refLeft !== refRight) {
      setZoomLeft(Math.min(refLeft, refRight));
      setZoomRight(Math.max(refLeft, refRight));
    }
    setRefLeft(null); setRefRight(null); setSelecting(false);
  };

  const toDisplayKm = (km) => units === 'imperial' ? km * 0.621371 : km;

  const chartData = useMemo(() => {
    if (!chunks || chunks.length === 0) return [];
    let cumDist = 0;
    let cumPowerNumer = 0;
    let cumPowerDenom = 0;
    return chunks.map((c) => {
      cumDist += c.distance || 0;
      if (c.avg_power != null && (c.duration || 0) > 0) {
        cumPowerNumer += c.avg_power * c.duration;
        cumPowerDenom += c.duration;
      }
      return {
        distance:      parseFloat(toDisplayKm(cumDist / 1000).toFixed(2)),
        altitude:      parseFloat(convertElevation(c.avg_altitude, units).toFixed(1)),
        speed:         parseFloat(convertSpeed(c.avg_speed, units).toFixed(1)),
        power:         c.avg_power != null ? parseFloat(c.avg_power.toFixed(0)) : null,
        avg_power_cum: cumPowerDenom > 0 ? parseFloat((cumPowerNumer / cumPowerDenom).toFixed(0)) : null,
        gradient:      parseFloat((c.avg_gradient || 0).toFixed(2)),
        vam:           c.stamina_score != null ? c.stamina_score : null,
      };
    });
  }, [chunks, units]); // eslint-disable-line react-hooks/exhaustive-deps

  const smoothed = useMemo(() => smoothData(chartData, smoothingWindow), [chartData, smoothingWindow]);

  const hasPower = chunks?.some(c => c.avg_power != null && c.avg_power > 0);

  const distLabel = `${t('distance', 'Distance')} (${units === 'imperial' ? 'mi' : 'km'})`;
  const speedLabel = units === 'imperial' ? 'mph' : 'km/h';
  const elevLabel = units === 'imperial' ? 'ft' : 'm';

  const formatMetric = (key, value) => {
    if (value == null) return 'N/A';
    switch (key) {
      case 'altitude':  return `${value.toFixed(0)} ${elevLabel}`;
      case 'speed':     return `${value.toFixed(1)} ${speedLabel}`;
      case 'power':
      case 'avg_power_cum': return `${Math.round(value)} W`;
      case 'gradient':  return `${value.toFixed(1)}%`;
      default:          return value.toFixed(2);
    }
  };

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    return (
      <Box sx={{ backgroundColor: colors.grey[800], border: `1px solid ${colors.grey[700]}`, borderRadius: '4px', p: 1.5 }}>
        <Typography variant="body2" fontWeight="bold" color={colors.grey[100]} mb={0.5}>
          {`${t('distance', 'Distance')}: ${payload[0]?.payload?.distance?.toFixed(2)} ${units === 'imperial' ? 'mi' : 'km'}`}
        </Typography>
        {payload.map((entry, i) => (
          <Typography key={i} variant="body2" color={entry.color}>
            {entry.name}: {formatMetric(entry.dataKey, entry.value)}
          </Typography>
        ))}
      </Box>
    );
  };

  if (!chunks || chunks.length === 0) return null;

  const metricOptions = [
    { key: 'speed',    label: t('speedLineName', 'Speed') },
    { key: 'gradient', label: t('gradientLineName', 'Gradient') },
    ...(hasPower ? [{ key: 'power',         label: t('powerLineName', 'Power') }] : []),
    ...(hasPower ? [{ key: 'avg_power_cum', label: t('avgPowerCumLineName', 'Avg Power (cumulative)') }] : []),
  ];

  const metricColors = {
    speed:         colors.chartColors[2],
    gradient:      colors.chartColors[3],
    power:         colors.chartColors[4],
    avg_power_cum: colors.chartColors[12],
  };

  return (
    <Paper elevation={3} sx={{ p: isMobile ? 1 : 3, backgroundColor: colors.grey[800], borderRadius: '8px', mb: 3 }}>
      <Box display="flex" flexDirection={isMobile ? 'column' : 'row'} justifyContent="space-between" alignItems={isMobile ? 'flex-start' : 'center'} mb={3} gap={isMobile ? 2 : 0}>
        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight="bold" color={colors.grey[100]}>
            {t('distanceBasedAnalysis', 'Distance-Based Analysis')}
          </Typography>
          {(zoomLeft !== 'dataMin' || zoomRight !== 'dataMax') && (
            <button
              onClick={resetZoom}
              style={{ background: colors.grey[700], color: colors.grey[100], border: `1px solid ${colors.grey[500]}`, borderRadius: 4, padding: '2px 10px', cursor: 'pointer', fontSize: '0.75rem' }}
            >
              {t('resetZoom', 'Reset Zoom')}
            </button>
          )}
        </Box>
        <Box display="flex" alignItems="center" gap={3} flexDirection={isMobile ? 'column' : 'row'} width={isMobile ? '100%' : 'auto'}>
          <Box sx={{ position: 'relative', border: `1px solid ${colors.grey[600]}`, borderRadius: '4px', px: 2, pt: 1, pb: 1, minWidth: 200 }}>
            <Typography variant="caption" sx={{ position: 'absolute', top: -10, left: 10, px: 0.5, backgroundColor: colors.grey[800], color: colors.grey[300], fontSize: '0.75rem' }}>
              {smoothingWindow === 1 ? t('smoothingOff', 'Smoothing (off)') : t('smoothingChunks', 'Smoothing ({{count}} chunks)', { count: smoothingWindow })}
            </Typography>
            <Slider value={smoothingWindow} min={1} max={20} step={1} onChange={(_, v) => setSmoothingWindow(v)} sx={{ color: colors.blueAccent[400], display: 'block', mt: 0.5 }} size="small" />
          </Box>
          <FormControl sx={{ minWidth: isMobile ? '100%' : 240, width: isMobile ? '100%' : 'auto' }}>
            <InputLabel sx={{ color: colors.grey[300] }}>{t('selectMetrics', 'Select Metrics')}</InputLabel>
            <Select
              multiple
              value={metrics}
              onChange={(e) => setMetrics(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
              input={<OutlinedInput label={t('selectMetrics', 'Select Metrics')} />}
              renderValue={(selected) => selected.map(m => metricOptions.find(o => o.key === m)?.label || m).join(', ')}
              sx={{ color: colors.grey[100], '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[600] } }}
            >
              {metricOptions.map(o => (
                <MenuItem key={o.key} value={o.key}>
                  <Checkbox checked={metrics.includes(o.key)} />
                  <ListItemText primary={o.label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Typography variant="caption" color={colors.grey[400]} mb={1} display="block">
        {t('dragToZoom', 'Drag on the chart to zoom in')}
      </Typography>

      <ResponsiveContainer width="100%" height={400}>
        <LineChart
          data={smoothed}
          margin={isMobile ? { top: 5, right: 5, left: 0, bottom: 10 } : { top: 5, right: 60, left: 10, bottom: 10 }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          style={{ userSelect: 'none' }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke={colors.grey[700]} />
          <XAxis
            dataKey="distance"
            type="number"
            domain={[zoomLeft, zoomRight]}
            allowDataOverflow
            stroke={colors.grey[100]}
            tickFormatter={(v) => v.toFixed(1)}
            tick={isMobile ? { fill: colors.grey[400], fontSize: 10 } : { fill: colors.grey[400], fontSize: 11 }}
          />
          <YAxis
            yAxisId="left"
            stroke={colors.grey[100]}
            width={isMobile ? 0 : 60}
            tick={!isMobile}
            axisLine={!isMobile}
            tickLine={!isMobile}
            label={isMobile ? undefined : { value: t('altitude', 'Altitude'), angle: -90, position: 'insideLeft', fill: colors.grey[300] }}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            stroke={colors.grey[100]}
            width={isMobile ? 0 : 60}
            tick={!isMobile}
            axisLine={!isMobile}
            tickLine={!isMobile}
            label={isMobile ? undefined : { value: t('metricsYAxis', 'Metrics'), angle: 90, position: 'insideRight', fill: colors.grey[300] }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend wrapperStyle={{ color: colors.grey[100] }} verticalAlign="bottom" />

          {/* Altitude always on left axis */}
          <Line yAxisId="left" type="monotone" dataKey="altitude" stroke={colors.chartColors[1]} name={t('altitudeLineName', 'Altitude')} dot={false} strokeWidth={2} />

          {/* Other metrics on right axis */}
          {metrics.includes('speed') && (
            <Line yAxisId="right" type="monotone" dataKey="speed" stroke={metricColors.speed} name={t('speedLineName', 'Speed')} dot={false} strokeWidth={2} />
          )}
          {metrics.includes('gradient') && (
            <Line yAxisId="right" type="monotone" dataKey="gradient" stroke={metricColors.gradient} name={t('gradientLineName', 'Gradient')} dot={false} strokeWidth={2} />
          )}
          {metrics.includes('power') && hasPower && (
            <Line yAxisId="right" type="monotone" dataKey="power" stroke={metricColors.power} name={t('powerLineName', 'Power')} dot={false} strokeWidth={2} />
          )}
          {metrics.includes('avg_power_cum') && hasPower && (
            <Line yAxisId="right" type="monotone" dataKey="avg_power_cum" stroke={metricColors.avg_power_cum} name={t('avgPowerCumLineName', 'Avg Power (cumulative)')} dot={false} strokeWidth={2} />
          )}

          {/* Climb category bands */}
          {climbs.map((climb, i) => {
            const x1 = toDisplayKm(climb.start_distance_m / 1000);
            const x2 = toDisplayKm(climb.end_distance_m / 1000);
            const fill = CLIMB_CATEGORY_FILL[climb.category] || CLIMB_CATEGORY_FILL['B-C4'];
            return <ReferenceArea key={`climb-${i}`} yAxisId="left" x1={x1} x2={x2} fill={fill} fillOpacity={0.22} stroke={fill} strokeOpacity={0.5} strokeWidth={1} ifOverflow="hidden" />;
          })}

          {/* Descent category bands */}
          {descents.map((descent, i) => {
            const x1 = toDisplayKm(descent.start_distance_m / 1000);
            const x2 = toDisplayKm(descent.end_distance_m / 1000);
            const fill = DESCENT_CATEGORY_FILL[descent.category] || DESCENT_CATEGORY_FILL['B-D4'];
            return <ReferenceArea key={`descent-${i}`} yAxisId="left" x1={x1} x2={x2} fill={fill} fillOpacity={0.22} stroke={fill} strokeOpacity={0.5} strokeWidth={1} ifOverflow="hidden" />;
          })}

          {/* Active climb highlight */}
          {activeClimbRange && (
            <ReferenceArea yAxisId="left" x1={toDisplayKm(activeClimbRange.leftKm)} x2={toDisplayKm(activeClimbRange.rightKm)} fill={activeClimbRange.color || '#e05000'} fillOpacity={0.35} stroke={activeClimbRange.color || '#e05000'} strokeOpacity={0.9} strokeWidth={2} ifOverflow="hidden" />
          )}

          {/* Active descent highlight */}
          {activeDescentRange && (
            <ReferenceArea yAxisId="left" x1={toDisplayKm(activeDescentRange.leftKm)} x2={toDisplayKm(activeDescentRange.rightKm)} fill={activeDescentRange.color || '#4090e0'} fillOpacity={0.35} stroke={activeDescentRange.color || '#4090e0'} strokeOpacity={0.9} strokeWidth={2} ifOverflow="hidden" />
          )}

          {/* Plan segment boundary lines — orange dashed */}
          {planSegments.length > 0 && (() => {
            const maxEnd = Math.max(...planSegments.map(s => s.end_distance_m));
            const seen = new Set();
            return planSegments
              .filter(seg => {
                const key = seg.end_distance_m.toFixed(0);
                if (seen.has(key) || seg.end_distance_m >= maxEnd) return false;
                seen.add(key);
                return true;
              })
              .map((seg, i) => (
                <ReferenceLine
                  key={`plan-boundary-${i}`}
                  yAxisId="left"
                  x={toDisplayKm(seg.end_distance_m / 1000)}
                  stroke="#fb923c"
                  strokeWidth={1}
                  strokeDasharray="4 3"
                  label={{ value: seg.end_label, position: 'top', fill: '#fb923c', fontSize: 9, angle: -45, offset: 4 }}
                />
              ));
          })()}

          {/* Active plan segment highlight — yellow */}
          {activePlanSegment && (
            <ReferenceArea
              yAxisId="left"
              x1={toDisplayKm(activePlanSegment.start_distance_m / 1000)}
              x2={toDisplayKm(activePlanSegment.end_distance_m / 1000)}
              fill="#ffeb3b"
              fillOpacity={0.25}
              stroke="#fb923c"
              strokeOpacity={0.9}
              strokeWidth={1.5}
              ifOverflow="hidden"
            />
          )}

          {/* Drag-to-zoom selection band */}
          {refLeft != null && refRight != null && (
            <ReferenceArea yAxisId="left" x1={Math.min(refLeft, refRight)} x2={Math.max(refLeft, refRight)} fill={colors.blueAccent[700]} fillOpacity={0.3} strokeOpacity={0.3} />
          )}
        </LineChart>
      </ResponsiveContainer>
    </Paper>
  );
};

export default BikeClimbsChart;
